import TRTC, {
  type ConnectionState,
  type EnterRoomConfig,
  type TRTC as TRTCClient
} from 'trtc-sdk-v5'
import { defineStore } from 'pinia'
import { computed, markRaw, reactive, ref, shallowRef, watch } from 'vue'
import { getUserSig } from '@/api/jiankao'
import { useMonitorRoomImageUpload } from '@/hooks/useMonitorRoomImageUpload'
import { useCameraStore } from './camera'
import { useDeviceDebugStore } from './deviceDebug'
import {
  MONITOR_SDK_APP_ID,
  type MonitorPosition,
  type MonitorPositionRoomConfig,
  type MonitorRoomGroup,
  buildMonitorPaperKeyFromActiInfo,
  buildMonitorPaperKeyFromPaper,
  collectMonitoredPaperKeys,
  hasLiveVideoTrack,
  parseMonitorRoomGroup
} from './monitorSessionUtils'
import { store } from './index'
import type { ActiInfo, ActivityItem, AnswerItem, PaperItem } from '@/api/activity/type'

export type MonitorSessionStatus = 'idle' | 'entering' | 'joined' | 'leaving' | 'error'
export type MonitorClientJoinStatus = 'idle' | 'entering' | 'joined' | 'leaving' | 'error'
export type MonitorPublishStatus = 'idle' | 'starting' | 'published' | 'error'

export interface MonitorPositionClientState {
  position: MonitorPosition
  roomCode: string
  userId: string
  userSig: string
  client: TRTCClient | null
  joinStatus: MonitorClientJoinStatus
  publishStatus: MonitorPublishStatus
  lastVideoTrackId: string
  lastAudioTrackId: string
  error: string
}

const MONITOR_SNAPSHOT_DELAY = 600
const MONITOR_SNAPSHOT_RETRY_TIMES = 5
const MONITOR_SNAPSHOT_RETRY_INTERVAL = 800

const SESSION_STATUS_TEXT: Record<MonitorSessionStatus, string> = {
  idle: '未连接',
  entering: '连接中',
  joined: '已连接',
  leaving: '退出中',
  error: '连接异常'
}

function createClientState(position: MonitorPosition): MonitorPositionClientState {
  return reactive({
    position,
    roomCode: '',
    userId: '',
    userSig: '',
    client: null,
    joinStatus: 'idle',
    publishStatus: 'idle',
    lastVideoTrackId: '',
    lastAudioTrackId: '',
    error: ''
  })
}

function resetClientState(state: MonitorPositionClientState) {
  state.roomCode = ''
  state.userId = ''
  state.userSig = ''
  state.client = null
  state.joinStatus = 'idle'
  state.publishStatus = 'idle'
  state.lastVideoTrackId = ''
  state.lastAudioTrackId = ''
  state.error = ''
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}

function wait(timeout: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, timeout)
  })
}

const SNAPSHOT_CAPTURE_TIMEOUT = 3000

async function captureFrameFromStream(stream?: MediaStream | null): Promise<Blob | null> {
  const videoTrack = stream?.getVideoTracks?.()[0]
  if (!videoTrack || videoTrack.readyState !== 'live') {
    return null
  }

  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  ;(video as any)['webkit-playsinline'] = true
  video.srcObject = new MediaStream([videoTrack])

  try {
    await video.play().catch(() => undefined)

    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      await new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          reject(new Error('snapshot video loaddata timeout'))
        }, SNAPSHOT_CAPTURE_TIMEOUT)
        const cleanup = () => {
          window.clearTimeout(timer)
          video.onloadeddata = null
          video.onerror = null
        }
        video.onloadeddata = () => {
          cleanup()
          resolve()
        }
        video.onerror = () => {
          cleanup()
          reject(new Error('snapshot video element error'))
        }
      })
    }

    const width = video.videoWidth
    const height = video.videoHeight
    if (!width || !height) {
      return null
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return null
    }
    ctx.drawImage(video, 0, 0, width, height)

    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85)
    })
  } finally {
    try {
      video.pause()
    } catch {
      // ignore
    }
    video.srcObject = null
  }
}

export const useMonitorSessionStore = defineStore(
  'APP_MONITOR_SESSION',
  () => {
    const cameraStore = useCameraStore()
    const deviceDebugStore = useDeviceDebugStore()
    const { uploadMonitorImageToOss } = useMonitorRoomImageUpload()

    const sessionStatus = ref<MonitorSessionStatus>('idle')
    const actiId = ref<number>()
    const actiLinkId = ref<number>()
    const activePaperKey = ref('')
    const roomGroupId = ref<number>()
    const roomGroupSignature = ref('')
    const monitoredPaperKeys = ref<string[]>([])
    const finishedPaperKeys = ref<string[]>([])
    const sessionError = ref('')
    const masterPreviewTarget = shallowRef<HTMLElement | null>(null)
    const capturedSnapshotPositions = ref<MonitorPosition[]>([])
    const uploadingSnapshotPositions = ref<MonitorPosition[]>([])
    const isRestoring = ref(false)
    let restorePromise: Promise<boolean> | null = null
    const snapshotTimers: Partial<Record<MonitorPosition, number>> = {}

    // 同位置的并发锁: 同一个 (position + key) 上正在进行的异步操作会复用同一个 Promise,
    // 避免 watch 与 ensureSessionRestored 等多路触发导致 TRTC SDK 抛 already start / not started.
    type InflightKey = 'join' | 'sync'
    const inflightLocks = new Map<string, Promise<void>>()
    const runWithInflight = (key: InflightKey, position: MonitorPosition, fn: () => Promise<void>) => {
      const lockKey = `${key}:${position}`
      const existed = inflightLocks.get(lockKey)
      if (existed) return existed
      const promise = fn().finally(() => {
        inflightLocks.delete(lockKey)
      })
      inflightLocks.set(lockKey, promise)
      return promise
    }
    const clearInflightLocks = () => {
      inflightLocks.clear()
    }

    const masterClientState = createClientState('master')
    const deskClientState = createClientState('desk')

    const getPositionState = (position: MonitorPosition) => {
      return position === 'master' ? masterClientState : deskClientState
    }

    const cameraSourceStream = computed(() => cameraStore.stream || deviceDebugStore.cameraStream)
    const hasSessionContext = computed(() => {
      return (
        !!actiId.value &&
        !!roomGroupSignature.value &&
        !!(masterClientState.roomCode || deskClientState.roomCode)
      )
    })
    const isSessionActive = computed(() => sessionStatus.value !== 'idle')
    const isJoined = computed(() => sessionStatus.value === 'joined')
    const shouldUseTrtcPreview = computed(() => {
      return (
        isJoined.value &&
        !!masterClientState.client &&
        masterClientState.publishStatus === 'published'
      )
    })
    const sessionMessage = computed(() => {
      return sessionError.value || SESSION_STATUS_TEXT[sessionStatus.value]
    })

    const setSessionError = (message: string) => {
      sessionError.value = message
      sessionStatus.value = 'error'
    }

    const syncSessionStatus = () => {
      if (sessionStatus.value === 'leaving') return
      const states = [masterClientState, deskClientState].filter((item) => item.roomCode)

      if (states.length === 0) {
        sessionStatus.value = 'idle'
        return
      }

      if (states.some((item) => item.joinStatus === 'error')) {
        sessionStatus.value = 'error'
        return
      }

      if (states.some((item) => item.joinStatus === 'entering')) {
        sessionStatus.value = 'entering'
        return
      }

      if (states.every((item) => item.joinStatus === 'joined')) {
        sessionStatus.value = 'joined'
        sessionError.value = ''
        return
      }

      sessionStatus.value = 'entering'
    }

    const hasCapturedSnapshot = (position: MonitorPosition) => {
      return capturedSnapshotPositions.value.includes(position)
    }

    const isSnapshotUploading = (position: MonitorPosition) => {
      return uploadingSnapshotPositions.value.includes(position)
    }

    const setSnapshotUploading = (position: MonitorPosition, uploading: boolean) => {
      if (uploading) {
        if (!isSnapshotUploading(position)) {
          uploadingSnapshotPositions.value = [...uploadingSnapshotPositions.value, position]
        }
        return
      }

      uploadingSnapshotPositions.value = uploadingSnapshotPositions.value.filter(
        (item) => item !== position
      )
    }

    const markSnapshotCaptured = (position: MonitorPosition) => {
      if (!hasCapturedSnapshot(position)) {
        capturedSnapshotPositions.value = [...capturedSnapshotPositions.value, position]
      }
    }

    const clearSnapshotTimer = (position: MonitorPosition) => {
      const timer = snapshotTimers[position]
      if (timer) {
        window.clearTimeout(timer)
        delete snapshotTimers[position]
      }
    }

    const resetSnapshotState = () => {
      clearSnapshotTimer('master')
      clearSnapshotTimer('desk')
      capturedSnapshotPositions.value = []
      uploadingSnapshotPositions.value = []
    }

    const getSnapshotSourceStream = (position: MonitorPosition) => {
      return position === 'master'
        ? cameraSourceStream.value
        : deviceDebugStore.screenStream
    }

    const uploadSnapshot = async (position: MonitorPosition) => {
      if (hasCapturedSnapshot(position) || isSnapshotUploading(position)) {
        return
      }

      const state = position === 'master' ? masterClientState : deskClientState
      if (!state.client || !actiLinkId.value || !roomGroupId.value) {
        return
      }

      setSnapshotUploading(position, true)

      try {
        let blob: Blob | null = null

        for (let attempt = 0; attempt < MONITOR_SNAPSHOT_RETRY_TIMES; attempt += 1) {
          try {
            blob = await captureFrameFromStream(getSnapshotSourceStream(position))
          } catch (error) {
            console.warn(`[monitor-session] capture ${position} frame failed`, error)
            blob = null
          }

          if (blob) {
            break
          }

          await wait(MONITOR_SNAPSHOT_RETRY_INTERVAL)
        }

        if (!blob) {
          return
        }

        await uploadMonitorImageToOss(blob, {
          actiLinkId: actiLinkId.value,
          roomGroupId: roomGroupId.value,
          position
        })
        markSnapshotCaptured(position)
      } catch (error) {
        console.warn(`[monitor-session] upload ${position} snapshot failed`, error)
      } finally {
        setSnapshotUploading(position, false)
      }
    }

    const scheduleSnapshotUpload = (position: MonitorPosition) => {
      if (hasCapturedSnapshot(position) || isSnapshotUploading(position)) {
        return
      }

      clearSnapshotTimer(position)
      snapshotTimers[position] = window.setTimeout(() => {
        uploadSnapshot(position).catch(() => undefined)
      }, MONITOR_SNAPSHOT_DELAY)
    }

    const applyRoomConfig = (
      state: MonitorPositionClientState,
      roomConfig?: MonitorPositionRoomConfig | null
    ) => {
      if (!roomConfig) {
        resetClientState(state)
        return
      }

      state.roomCode = roomConfig.roomCode
      state.userId = roomConfig.userId
    }

    const joinPositionRoomImpl = async (position: MonitorPosition) => {
      const state = getPositionState(position)
      if (!state.roomCode || !state.userId) return
      if (state.joinStatus === 'joined') {
        return
      }

      if (state.joinStatus === 'error' && state.client) {
        const roomCode = state.roomCode
        const userId = state.userId
        await destroyClientState(state)
        state.roomCode = roomCode
        state.userId = userId
      }

      const client = ensureClient(state)
      state.joinStatus = 'entering'
      state.error = ''
      sessionStatus.value = 'entering'
      sessionError.value = ''

      const { userSig } = await getUserSig({
        roomCode: state.roomCode
      })

      state.userSig = userSig

      const enterRoomConfig: EnterRoomConfig = {
        sdkAppId: MONITOR_SDK_APP_ID,
        userId: state.userId,
        userSig,
        strRoomId: state.roomCode,
        role: TRTC.TYPE.ROLE_ANCHOR,
        scene: TRTC.TYPE.SCENE_RTC
      }

      await client.enterRoom(enterRoomConfig)
      state.joinStatus = 'joined'
      state.error = ''
      syncSessionStatus()
    }

    const joinPositionRoom = (position: MonitorPosition) => {
      return runWithInflight('join', position, () => joinPositionRoomImpl(position))
    }

    const getMasterTracks = () => {
      const stream = cameraSourceStream.value
      const videoTrack = stream?.getVideoTracks?.()[0]
      const audioTrack = stream?.getAudioTracks?.()[0]

      if (!videoTrack || videoTrack.readyState !== 'live') {
        throw new Error('主摄像头未准备好，请先完成设备调试')
      }

      if (!audioTrack || audioTrack.readyState !== 'live') {
        throw new Error('麦克风未准备好，请先完成设备调试')
      }

      return {
        videoTrack,
        audioTrack
      }
    }

    const getDeskTracks = () => {
      const stream = deviceDebugStore.screenStream
      const videoTrack = stream?.getVideoTracks?.()[0]
      const audioTrack = stream?.getAudioTracks?.()[0]

      if (!videoTrack || videoTrack.readyState !== 'live') {
        throw new Error('屏幕共享未准备好，请先完成屏幕共享检测')
      }

      return {
        videoTrack,
        audioTrack
      }
    }

    const bindMasterPreview = async (element?: HTMLElement | null) => {
      masterPreviewTarget.value = element || null

      if (!masterClientState.client || masterClientState.joinStatus !== 'joined') {
        return
      }

      try {
        await masterClientState.client.updateLocalVideo({
          view: masterPreviewTarget.value || null
        })
      } catch (error) {
        console.warn('[monitor-session] bind master preview failed', error)
      }
    }

    const attachClientEvents = (
      state: MonitorPositionClientState,
      client: TRTCClient,
      positionLabel: string
    ) => {
      client.on(TRTC.EVENT.CONNECTION_STATE_CHANGED, ({ state: nextState }) => {
        const statusMap: Partial<Record<ConnectionState, MonitorClientJoinStatus>> = {
          CONNECTING: 'entering',
          RECONNECTING: 'entering',
          RECONNECTED: 'joined',
          CONNECTED: 'joined',
          DISCONNECTED: 'error'
        }

        const nextJoinStatus = statusMap[nextState]
        if (nextJoinStatus) {
          state.joinStatus = nextJoinStatus
          if (nextJoinStatus === 'error') {
            state.error = `${positionLabel}连接已断开`
            sessionError.value = state.error
          }
          syncSessionStatus()
        }
      })

      client.on(TRTC.EVENT.ERROR, (error) => {
        state.error = `${positionLabel}${error.message || '发生异常'}`
        state.publishStatus = 'error'
        if (state.joinStatus !== 'joined') {
          state.joinStatus = 'error'
          setSessionError(state.error)
        } else {
          syncSessionStatus()
        }
      })

      client.on(TRTC.EVENT.KICKED_OUT, ({ reason }) => {
        state.error = `${positionLabel}已被移出房间(${reason})`
        state.joinStatus = 'error'
        state.publishStatus = 'error'
        setSessionError(state.error)
      })

      client.on(TRTC.EVENT.PUBLISH_STATE_CHANGED, ({ mediaType, state: publishState, error }) => {
        if (publishState === 'starting') {
          state.publishStatus = 'starting'
          syncSessionStatus()
          return
        }

        if (publishState === 'started') {
          state.publishStatus = 'published'
          scheduleSnapshotUpload(state.position)
          syncSessionStatus()
          return
        }

        if (publishState === 'stopped' && error) {
          state.publishStatus = 'error'
          state.error = `${positionLabel}${mediaType}推流失败: ${error.message || '请重试'}`
          syncSessionStatus()
        }
      })

      client.on(TRTC.EVENT.FIRST_VIDEO_FRAME, (event) => {
        if (event.userId) {
          return
        }

        if (state.position === 'master' && event.streamType === TRTC.TYPE.STREAM_TYPE_MAIN) {
          scheduleSnapshotUpload('master')
        }

        if (state.position === 'desk' && event.streamType === TRTC.TYPE.STREAM_TYPE_SUB) {
          scheduleSnapshotUpload('desk')
        }
      })

      if (state.position === 'desk') {
        client.on(TRTC.EVENT.SCREEN_SHARE_STOPPED, () => {
          state.publishStatus = 'error'
          state.error = '屏幕共享已中断，请重新开启'
          deviceDebugStore.markScreenShareInterrupted(state.error)
          syncSessionStatus()
        })
      }
    }

    const ensureClient = (state: MonitorPositionClientState) => {
      if (state.client) {
        return state.client
      }

      const client = markRaw(TRTC.create())
      attachClientEvents(state, client, state.position === 'master' ? '主机位' : '屏幕监控')
      state.client = client
      return client
    }

    const destroyClientState = async (state: MonitorPositionClientState) => {
      const client = state.client
      if (!client) {
        resetClientState(state)
        return
      }

      try {
        if (state.joinStatus === 'joined' || state.joinStatus === 'entering') {
          await client.exitRoom().catch(() => undefined)
        }
      } finally {
        client.destroy()
        resetClientState(state)
      }
    }

    const isPublishing = (status: MonitorPublishStatus) => {
      return status === 'published' || status === 'starting'
    }

    const syncMasterTracksImpl = async () => {
      if (!masterClientState.client || masterClientState.joinStatus !== 'joined') {
        return
      }

      const { videoTrack, audioTrack } = getMasterTracks()
      const view = masterPreviewTarget.value || null
      const needStart = !isPublishing(masterClientState.publishStatus)

      try {
        if (needStart) {
          masterClientState.publishStatus = 'starting'
          await masterClientState.client.startLocalVideo({
            publish: true,
            view,
            option: { videoTrack }
          })
          await masterClientState.client.startLocalAudio({
            publish: true,
            option: { audioTrack }
          })
        } else {
          if (masterClientState.lastVideoTrackId !== videoTrack.id || masterPreviewTarget.value) {
            await masterClientState.client.updateLocalVideo({
              view,
              publish: true,
              option: { videoTrack }
            })
          }

          if (masterClientState.lastAudioTrackId !== audioTrack.id) {
            await masterClientState.client.updateLocalAudio({
              publish: true,
              option: { audioTrack }
            })
          }
        }

        masterClientState.lastVideoTrackId = videoTrack.id
        masterClientState.lastAudioTrackId = audioTrack.id
        masterClientState.publishStatus = 'published'
        masterClientState.error = ''
        scheduleSnapshotUpload('master')
        syncSessionStatus()
      } catch (error) {
        masterClientState.publishStatus = 'error'
        masterClientState.error = getErrorMessage(error, '主机位推流更新失败')
        syncSessionStatus()
        throw error
      }
    }

    const syncMasterTracks = () => {
      return runWithInflight('sync', 'master', syncMasterTracksImpl)
    }

    const syncDeskTracksImpl = async () => {
      if (!deskClientState.client || deskClientState.joinStatus !== 'joined') {
        return
      }

      const { videoTrack, audioTrack } = getDeskTracks()
      const needStart = !isPublishing(deskClientState.publishStatus)

      try {
        if (needStart) {
          deskClientState.publishStatus = 'starting'
          await deskClientState.client.startScreenShare({
            view: null,
            publish: true,
            option: { videoTrack, audioTrack }
          })
        } else {
          await deskClientState.client.updateScreenShare({
            view: null,
            publish: true,
            option: { videoTrack, audioTrack }
          })
        }

        deskClientState.lastVideoTrackId = videoTrack.id
        deskClientState.lastAudioTrackId = audioTrack?.id || ''
        deskClientState.publishStatus = 'published'
        deskClientState.error = ''
        scheduleSnapshotUpload('desk')
        syncSessionStatus()
      } catch (error) {
        deskClientState.publishStatus = 'error'
        deskClientState.error = getErrorMessage(error, '屏幕监控推流更新失败')
        syncSessionStatus()
        throw error
      }
    }

    const syncDeskTracks = () => {
      return runWithInflight('sync', 'desk', syncDeskTracksImpl)
    }

    // 监听本地媒体流变化, 自动续推到 TRTC. 失败状态由 sync*Tracks / enterPositionRoom 内部统一维护.
    const watchPositionStream = (
      position: MonitorPosition,
      streamGetter: () => MediaStream | undefined | null
    ) => {
      const state = getPositionState(position)
      const sync = position === 'master' ? syncMasterTracks : syncDeskTracks
      watch(
        streamGetter,
        async (stream, oldStream) => {
          if (!stream || stream === oldStream || !hasSessionContext.value || !state.roomCode) {
            return
          }

          if (!state.client || state.joinStatus !== 'joined') {
            await enterPositionRoom(position).catch(() => undefined)
            return
          }

          await sync().catch(() => undefined)
        },
        { flush: 'post' }
      )
    }

    watchPositionStream('master', () => cameraSourceStream.value)
    watchPositionStream('desk', () => deviceDebugStore.screenStream)

    const prepareSessionFromPaper = async ({
      paperItem,
      childrenPaperItem,
      activityItem
    }: {
      paperItem: PaperItem
      childrenPaperItem?: AnswerItem
      activityItem?: ActivityItem
    }) => {
      if (actiId.value && actiId.value !== paperItem.actiId && isSessionActive.value) {
        await destroyClientState(masterClientState)
        await destroyClientState(deskClientState)
        resetSnapshotState()
        sessionStatus.value = 'idle'
        sessionError.value = ''
        finishedPaperKeys.value = []
      }

      const roomGroup = parseMonitorRoomGroup(paperItem.monitorRoomList?.[0])
      if (!roomGroup) {
        throw new Error('当前问卷未配置有效的监考房间，请联系管理员')
      }

      if (paperItem.isOpenMasterMonitor && !roomGroup.positions.master) {
        throw new Error('当前问卷缺少主机位房间配置，请联系管理员')
      }

      if (paperItem.isOpenDeskMonitor && !roomGroup.positions.desk) {
        throw new Error('当前问卷缺少屏幕监控房间配置，请联系管理员')
      }

      if (
        actiId.value === paperItem.actiId &&
        roomGroupSignature.value &&
        roomGroupSignature.value !== roomGroup.signature
      ) {
        throw new Error('同一活动的监考房间配置不一致，请联系管理员')
      }

      actiId.value = paperItem.actiId
      actiLinkId.value = paperItem.actiLinkId
      roomGroupId.value = roomGroup.roomGroupId
      roomGroupSignature.value = roomGroup.signature
      monitoredPaperKeys.value = collectMonitoredPaperKeys(activityItem)
      activePaperKey.value = buildMonitorPaperKeyFromPaper(paperItem, childrenPaperItem)

      applyRoomConfig(
        masterClientState,
        paperItem.isOpenMasterMonitor ? roomGroup.positions.master : null
      )
      applyRoomConfig(
        deskClientState,
        paperItem.isOpenDeskMonitor ? roomGroup.positions.desk : null
      )

      return roomGroup
    }

    const enterPositionRoom = async (position: MonitorPosition) => {
      const state = getPositionState(position)
      if (!state.roomCode || !state.userId) return
      if (state.joinStatus === 'joined' && state.publishStatus === 'published') {
        return
      }

      const sync = position === 'master' ? syncMasterTracks : syncDeskTracks

      if (state.joinStatus === 'joined') {
        await sync()
        return
      }

      try {
        await joinPositionRoom(position)
        await sync()
        syncSessionStatus()
      } catch (error) {
        const message = getErrorMessage(
          error,
          position === 'master' ? '主机位进房失败' : '屏幕监控进房失败'
        )
        // state 是 reactive, 运行期 joinStatus 可能已被 client 事件回调改写, 这里要用运行期值判断.
        const liveJoinStatus = state.joinStatus as MonitorClientJoinStatus
        if (liveJoinStatus !== 'joined') {
          state.joinStatus = 'error'
          state.error = message
          setSessionError(message)
        }
        throw error
      }
    }

    const ensureSessionReady = async ({
      paperItem,
      childrenPaperItem,
      activityItem
    }: {
      paperItem: PaperItem
      childrenPaperItem?: AnswerItem
      activityItem?: ActivityItem
    }) => {
      await prepareSessionFromPaper({
        paperItem,
        childrenPaperItem,
        activityItem
      })

      try {
        if (paperItem.isOpenMasterMonitor) {
          await enterPositionRoom('master')
        }

        if (paperItem.isOpenDeskMonitor) {
          await enterPositionRoom('desk')
        }

        syncSessionStatus()
      } catch (error) {
        await leaveRoomOnce('session_reset')
        throw new Error(getErrorMessage(error, '监考房间连接失败，请稍后重试'))
      }
    }

    const ensureSessionRestored = async () => {
      if (!hasSessionContext.value) {
        return false
      }

      if (restorePromise) {
        return restorePromise
      }

      restorePromise = (async () => {
        isRestoring.value = true
        sessionStatus.value = 'entering'
        sessionError.value = ''

        try {
          if (masterClientState.roomCode) {
            await joinPositionRoom('master')
            await deviceDebugStore.checkCamera().catch(() => undefined)

            if (hasLiveVideoTrack(cameraSourceStream.value)) {
              await syncMasterTracks().catch(() => undefined)
            }
          }

          if (deskClientState.roomCode) {
            await joinPositionRoom('desk')

            if (hasLiveVideoTrack(deviceDebugStore.screenStream)) {
              await syncDeskTracks().catch(() => undefined)
            } else {
              // 浏览器安全策略下屏幕共享授权无法持久化, 刷新后必然要求用户重新授权.
              const message = '页面刷新后屏幕共享已中断，请重新开启屏幕共享'
              deskClientState.publishStatus = 'error'
              deskClientState.error = message
              deviceDebugStore.markScreenShareInterrupted(message)
            }
          }

          syncSessionStatus()

          return (sessionStatus.value as MonitorSessionStatus) === 'joined'
        } catch (error) {
          setSessionError(getErrorMessage(error, '监考房间恢复失败，请稍后重试'))
          return false
        } finally {
          isRestoring.value = false
          restorePromise = null
        }
      })()

      return restorePromise
    }

    const markPaperFinished = (paperKey: string) => {
      if (!paperKey || !monitoredPaperKeys.value.includes(paperKey)) {
        return false
      }

      if (!finishedPaperKeys.value.includes(paperKey)) {
        finishedPaperKeys.value = [...finishedPaperKeys.value, paperKey]
      }

      return monitoredPaperKeys.value.every((key) => finishedPaperKeys.value.includes(key))
    }

    const leaveRoomOnce = async (
      reason: 'last_paper_finished' | 'force_quit' | 'session_reset' = 'session_reset'
    ) => {
      if (
        !hasSessionContext.value &&
        !isSessionActive.value &&
        !masterClientState.client &&
        !deskClientState.client
      ) {
        return
      }

      sessionStatus.value = 'leaving'
      sessionError.value = reason === 'force_quit' ? '监考会话已结束' : ''
      actiId.value = undefined
      actiLinkId.value = undefined
      activePaperKey.value = ''
      roomGroupId.value = undefined
      roomGroupSignature.value = ''
      monitoredPaperKeys.value = []
      finishedPaperKeys.value = []
      masterPreviewTarget.value = null
      isRestoring.value = false
      restorePromise = null
      clearInflightLocks()

      await Promise.all([
        destroyClientState(masterClientState),
        destroyClientState(deskClientState)
      ])
      resetSnapshotState()

      sessionStatus.value = 'idle'
      sessionError.value = ''
    }

    const leaveSessionIfLastPaper = async (actiInfo?: Partial<ActiInfo>) => {
      const paperKey = buildMonitorPaperKeyFromActiInfo(actiInfo)
      if (!paperKey) return false

      const isLast = markPaperFinished(paperKey)
      if (isLast) {
        await leaveRoomOnce('last_paper_finished')
      }

      return isLast
    }

    const resetSession = async () => {
      await leaveRoomOnce('session_reset')
    }

    return {
      sessionStatus,
      actiId,
      actiLinkId,
      activePaperKey,
      roomGroupId,
      roomGroupSignature,
      monitoredPaperKeys,
      finishedPaperKeys,
      sessionError,
      sessionMessage,
      hasSessionContext,
      masterClientState,
      deskClientState,
      isSessionActive,
      isJoined,
      isRestoring,
      shouldUseTrtcPreview,
      prepareSessionFromPaper,
      ensureSessionReady,
      ensureSessionRestored,
      enterPositionRoom,
      bindMasterPreview,
      markPaperFinished,
      leaveRoomOnce,
      leaveSessionIfLastPaper,
      resetSession
    }
  },
  {
    persist: {
      paths: [
        'actiId',
        'actiLinkId',
        'activePaperKey',
        'roomGroupId',
        'roomGroupSignature',
        'monitoredPaperKeys',
        'finishedPaperKeys',
        'masterClientState.roomCode',
        'masterClientState.userId',
        'deskClientState.roomCode',
        'deskClientState.userId'
      ]
    }
  }
)

export function useMonitorSessionStoreWithOut() {
  return useMonitorSessionStore(store)
}
